export interface BlockDefinition {
  id: string;
  minecraftName: string;
  name: string;
  shortName: string;
  color: string;
  palette: string[];
  pattern: string;
  textures?: Record<string, string>;
  faceTints?: Record<string, string>;
  transparent?: boolean;
  opacity?: number;
  alphaTest?: number;
  emissive?: string;
  emissiveIntensity?: number;
  tint?: string;
  defaultState?: Record<string, string>;
  stateOptions?: Record<string, string[]>;
  connectionType?: "wall" | "fence" | "pane";
  placementType?: "door" | "bed" | "double_plant";
  availableInVersion?: string;
  category?: string;
}

export const BLOCK_DEFINITIONS: BlockDefinition[] = [
  {
    id: "stone_bricks",
    minecraftName: "minecraft:stone_bricks",
    name: "Stone Bricks",
    shortName: "Stone",
    color: "#87909a",
    palette: ["#6f7780", "#818a94", "#939ca5", "#737c86"],
    pattern: "brick",
    textures: { all: "block/stone_bricks" }
  },
  {
    id: "cracked_stone_bricks",
    minecraftName: "minecraft:cracked_stone_bricks",
    name: "Cracked Stone Bricks",
    shortName: "Crack",
    color: "#757d85",
    palette: ["#5f666d", "#737a81", "#858d94", "#666d74"],
    pattern: "crackedBrick",
    textures: { all: "block/cracked_stone_bricks" }
  },
  {
    id: "mossy_stone_bricks",
    minecraftName: "minecraft:mossy_stone_bricks",
    name: "Mossy Stone Bricks",
    shortName: "Moss",
    color: "#6f806b",
    palette: ["#667263", "#77846c", "#59665a", "#88917a"],
    pattern: "mossyBrick",
    textures: { all: "block/mossy_stone_bricks" }
  },
  {
    id: "cobblestone",
    minecraftName: "minecraft:cobblestone",
    name: "Cobblestone",
    shortName: "Cobble",
    color: "#777a7a",
    palette: ["#636666", "#777a7a", "#8a8d8d", "#6d7070"],
    pattern: "stone",
    textures: { all: "block/cobblestone" }
  },
  {
    id: "polished_andesite",
    minecraftName: "minecraft:polished_andesite",
    name: "Polished Andesite",
    shortName: "Ande",
    color: "#949a9b",
    palette: ["#848a8c", "#969c9d", "#a5aaab", "#8b9192"],
    pattern: "tile",
    textures: { all: "block/polished_andesite" }
  },
  {
    id: "deepslate_tiles",
    minecraftName: "minecraft:deepslate_tiles",
    name: "Deepslate Tiles",
    shortName: "Deep",
    color: "#45494f",
    palette: ["#383c42", "#454a50", "#51565d", "#3e4349"],
    pattern: "brick",
    textures: { all: "block/deepslate_tiles" }
  },
  {
    id: "spruce_planks",
    minecraftName: "minecraft:spruce_planks",
    name: "Spruce Planks",
    shortName: "Spruce",
    color: "#785736",
    palette: ["#65472d", "#765535", "#896341", "#6d4e31"],
    pattern: "plank",
    textures: { all: "block/spruce_planks" }
  },
  {
    id: "spruce_log",
    minecraftName: "minecraft:spruce_log",
    name: "Spruce Log",
    shortName: "S.Log",
    color: "#5d432a",
    palette: ["#4d3723", "#60452a", "#6f5030", "#553c26"],
    pattern: "log",
    textures: {
      side: "block/spruce_log",
      top: "block/spruce_log_top",
      bottom: "block/spruce_log_top"
    },
    defaultState: { axis: "y" },
    stateOptions: { axis: ["x", "y", "z"] }
  },
  {
    id: "dark_oak_planks",
    minecraftName: "minecraft:dark_oak_planks",
    name: "Dark Oak Planks",
    shortName: "D.Oak",
    color: "#4b3422",
    palette: ["#3b291c", "#4d3522", "#5d3f29", "#432e1e"],
    pattern: "plank",
    textures: { all: "block/dark_oak_planks" }
  },
  {
    id: "dark_oak_log",
    minecraftName: "minecraft:dark_oak_log",
    name: "Dark Oak Log",
    shortName: "D.Log",
    color: "#392c22",
    palette: ["#2d231b", "#3b2d22", "#49372a", "#33271e"],
    pattern: "log",
    textures: {
      side: "block/dark_oak_log",
      top: "block/dark_oak_log_top",
      bottom: "block/dark_oak_log_top"
    },
    defaultState: { axis: "y" },
    stateOptions: { axis: ["x", "y", "z"] }
  },
  {
    id: "bricks",
    minecraftName: "minecraft:bricks",
    name: "Bricks",
    shortName: "Brick",
    color: "#935d50",
    palette: ["#7e4d43", "#935c4e", "#a86b5b", "#865248"],
    pattern: "brick",
    textures: { all: "block/bricks" }
  },
  {
    id: "calcite",
    minecraftName: "minecraft:calcite",
    name: "Calcite",
    shortName: "Calc",
    color: "#d7d4c8",
    palette: ["#c4c2b9", "#d9d6cc", "#e4e1d7", "#cecbc1"],
    pattern: "stone",
    textures: { all: "block/calcite" }
  },
  {
    id: "glass",
    minecraftName: "minecraft:glass",
    name: "Glass",
    shortName: "Glass",
    color: "#9dc7d2",
    palette: ["#85b7c3", "#a3ced7", "#b2d9e0", "#8fc0cb"],
    pattern: "glass",
    textures: { all: "block/glass" },
    transparent: true,
    opacity: 0.58,
    alphaTest: 0.02
  },
  {
    id: "iron_bars",
    minecraftName: "minecraft:iron_bars",
    name: "Iron Bars",
    shortName: "Iron",
    color: "#a6afb4",
    palette: ["#8f989e", "#a7b0b5", "#bec5c8", "#969fa4"],
    pattern: "metal",
    textures: { all: "block/iron_bars" },
    transparent: true,
    alphaTest: 0.35,
    connectionType: "pane",
    defaultState: {
      east: "false",
      north: "false",
      south: "false",
      west: "false",
      waterlogged: "false"
    }
  },
  {
    id: "lantern",
    minecraftName: "minecraft:lantern",
    name: "Lantern",
    shortName: "Light",
    color: "#d9a847",
    palette: ["#8e6d2f", "#d5a447", "#efca72", "#b98939"],
    pattern: "metal",
    textures: { all: "block/lantern" },
    transparent: true,
    alphaTest: 0.25,
    emissive: "#6c4b15",
    emissiveIntensity: 0.34,
    defaultState: { hanging: "false", waterlogged: "false" },
    stateOptions: { hanging: ["false", "true"] }
  },
  {
    id: "grass_block",
    minecraftName: "minecraft:grass_block",
    name: "Grass Block",
    shortName: "Grass",
    color: "#6f9a55",
    palette: ["#587e43", "#6f9853", "#80aa61", "#648c4c"],
    pattern: "grass",
    textures: {
      side: "block/grass_block_side",
      top: "block/grass_block_top",
      bottom: "block/dirt"
    },
    faceTints: {
      top: "#7fbd62"
    },
    defaultState: { snowy: "false" },
    stateOptions: { snowy: ["false", "true"] }
  },
  {
    id: "dirt",
    minecraftName: "minecraft:dirt",
    name: "Dirt",
    shortName: "Dirt",
    color: "#806044",
    palette: ["#6f523a", "#805f43", "#936f4f", "#76583e"],
    pattern: "soil",
    textures: { all: "block/dirt" }
  },
  {
    id: "gravel",
    minecraftName: "minecraft:gravel",
    name: "Gravel",
    shortName: "Gravel",
    color: "#85817c",
    palette: ["#716e69", "#827f7a", "#96918b", "#7b7772"],
    pattern: "stone",
    textures: { all: "block/gravel" }
  },
  {
    id: "oak_leaves",
    minecraftName: "minecraft:oak_leaves[persistent=true]",
    name: "Oak Leaves",
    shortName: "Leaves",
    color: "#537d45",
    palette: ["#3f6635", "#527b43", "#668f54", "#496f3d"],
    pattern: "leaves",
    textures: { all: "block/oak_leaves" },
    tint: "#58a83d",
    transparent: true,
    alphaTest: 0.3,
    opacity: 1,
    defaultState: {
      distance: "7",
      persistent: "true",
      waterlogged: "false"
    }
  },
  {
    id: "stone_brick_stairs",
    minecraftName: "minecraft:stone_brick_stairs",
    name: "Stone Brick Stairs",
    shortName: "Stairs",
    color: "#858d96",
    palette: ["#707880", "#858d96", "#969ea5", "#777f87"],
    pattern: "brick",
    textures: { all: "block/stone_bricks" },
    defaultState: {
      facing: "north",
      half: "bottom",
      shape: "straight",
      waterlogged: "false"
    },
    stateOptions: {
      facing: ["north", "east", "south", "west"],
      half: ["bottom", "top"],
      shape: ["straight", "inner_left", "inner_right", "outer_left", "outer_right"]
    }
  },
  {
    id: "stone_brick_slab",
    minecraftName: "minecraft:stone_brick_slab",
    name: "Stone Brick Slab",
    shortName: "Slab",
    color: "#858d96",
    palette: ["#707880", "#858d96", "#969ea5", "#777f87"],
    pattern: "brick",
    textures: { all: "block/stone_bricks" },
    defaultState: { type: "bottom", waterlogged: "false" },
    stateOptions: { type: ["bottom", "top", "double"] }
  },
  {
    id: "stone_brick_wall",
    minecraftName: "minecraft:stone_brick_wall",
    name: "Stone Brick Wall",
    shortName: "Wall",
    color: "#7f878f",
    palette: ["#6d747b", "#7f878f", "#9199a0", "#737b82"],
    pattern: "brick",
    textures: { all: "block/stone_bricks" },
    connectionType: "wall",
    defaultState: {
      east: "none",
      north: "none",
      south: "none",
      west: "none",
      up: "true",
      waterlogged: "false"
    }
  },
  {
    id: "spruce_fence",
    minecraftName: "minecraft:spruce_fence",
    name: "Spruce Fence",
    shortName: "Fence",
    color: "#6f5032",
    palette: ["#5c412a", "#6f5032", "#805d3a", "#65482d"],
    pattern: "plank",
    textures: { all: "block/spruce_planks" },
    connectionType: "fence",
    defaultState: {
      east: "false",
      north: "false",
      south: "false",
      west: "false",
      waterlogged: "false"
    }
  },
  {
    id: "glass_pane",
    minecraftName: "minecraft:glass_pane",
    name: "Glass Pane",
    shortName: "Pane",
    color: "#9dc7d2",
    palette: ["#85b7c3", "#a3ced7", "#b2d9e0", "#8fc0cb"],
    pattern: "glass",
    textures: { all: "block/glass" },
    connectionType: "pane",
    transparent: true,
    opacity: 0.72,
    alphaTest: 0.02,
    defaultState: {
      east: "false",
      north: "false",
      south: "false",
      west: "false",
      waterlogged: "false"
    }
  },
  {
    id: "spruce_door",
    minecraftName: "minecraft:spruce_door",
    name: "Spruce Door",
    shortName: "Door",
    color: "#745334",
    palette: ["#60432a", "#745334", "#89613b", "#68492d"],
    pattern: "plank",
    transparent: true,
    alphaTest: 0.2,
    placementType: "door",
    defaultState: {
      facing: "north",
      half: "lower",
      hinge: "left",
      open: "false",
      powered: "false"
    },
    stateOptions: {
      facing: ["north", "east", "south", "west"],
      hinge: ["left", "right"],
      open: ["false", "true"]
    }
  },
  {
    id: "spruce_trapdoor",
    minecraftName: "minecraft:spruce_trapdoor",
    name: "Spruce Trapdoor",
    shortName: "Trap",
    color: "#745334",
    palette: ["#60432a", "#745334", "#89613b", "#68492d"],
    pattern: "plank",
    transparent: true,
    alphaTest: 0.2,
    defaultState: {
      facing: "north",
      half: "bottom",
      open: "false",
      powered: "false",
      waterlogged: "false"
    },
    stateOptions: {
      facing: ["north", "east", "south", "west"],
      half: ["bottom", "top"],
      open: ["false", "true"]
    }
  },
  {
    id: "deepslate_tile_stairs",
    minecraftName: "minecraft:deepslate_tile_stairs",
    name: "Deepslate Tile Stairs",
    shortName: "D.Stair",
    color: "#45494f",
    palette: ["#383c42", "#454a50", "#51565d", "#3e4349"],
    pattern: "brick",
    textures: { all: "block/deepslate_tiles" },
    defaultState: {
      facing: "north",
      half: "bottom",
      shape: "straight",
      waterlogged: "false"
    },
    stateOptions: {
      facing: ["north", "east", "south", "west"],
      half: ["bottom", "top"],
      shape: ["straight", "inner_left", "inner_right", "outer_left", "outer_right"]
    }
  },
  {
    id: "deepslate_tile_slab",
    minecraftName: "minecraft:deepslate_tile_slab",
    name: "Deepslate Tile Slab",
    shortName: "D.Slab",
    color: "#45494f",
    palette: ["#383c42", "#454a50", "#51565d", "#3e4349"],
    pattern: "brick",
    textures: { all: "block/deepslate_tiles" },
    defaultState: { type: "bottom", waterlogged: "false" },
    stateOptions: { type: ["bottom", "top", "double"] }
  },
  {
    id: "water",
    minecraftName: "minecraft:water",
    name: "Water",
    shortName: "Water",
    color: "#3f7ca5",
    palette: ["#32698e", "#3f7aa2", "#5594ba", "#39739b"],
    pattern: "water",
    textures: { all: "block/water_still" },
    tint: "#4b83d8",
    transparent: true,
    opacity: 0.72
  }
];

const CURATED_BLOCK_DEFINITIONS = BLOCK_DEFINITIONS.map((definition) => ({
  ...definition,
  category: definition.category ?? "Building"
}));

export const BLOCK_MAP = new Map<string, BlockDefinition>(
  BLOCK_DEFINITIONS.map((definition) => [definition.id, definition])
);

const BLOCK_ID_COMPATIBILITY_GROUPS: ReadonlyArray<
  ReadonlyArray<string>
> = [
  ["chain", "iron_chain"]
];

export function resolveBlockId(blockId: string): string {
  const normalizedBlockId = String(blockId ?? "")
    .trim()
    .replace(/^minecraft:/, "");

  if (!normalizedBlockId || BLOCK_MAP.has(normalizedBlockId)) {
    return normalizedBlockId;
  }

  for (const compatibleIds of BLOCK_ID_COMPATIBILITY_GROUPS) {
    if (!compatibleIds.includes(normalizedBlockId)) {
      continue;
    }

    const availableReplacement = compatibleIds.find(
      (candidateId) =>
        candidateId !== normalizedBlockId &&
        BLOCK_MAP.has(candidateId)
    );

    if (availableReplacement) {
      return availableReplacement;
    }
  }

  return normalizedBlockId;
}

export function installVersionBlockCatalog(
  definitions: BlockDefinition[]
): void {
  const curatedById = new Map(
    CURATED_BLOCK_DEFINITIONS.map((definition) => [
      definition.id,
      definition
    ])
  );

  const merged = definitions.map((definition) => {
    const curated = curatedById.get(definition.id);
    if (!curated) return definition;

    const versionStateOptions = definition.stateOptions ?? {};
    const defaultState = { ...(definition.defaultState ?? {}) };

    Object.entries(curated.defaultState ?? {}).forEach(
      ([key, value]) => {
        const allowedValues = versionStateOptions[key];
        if (!allowedValues || allowedValues.includes(value)) {
          defaultState[key] = value;
        }
      }
    );

    return {
      ...definition,
      ...curated,
      availableInVersion: definition.availableInVersion,
      category: curated.category ?? definition.category,
      defaultState,
      stateOptions: versionStateOptions,
      connectionType:
        definition.connectionType ?? curated.connectionType,
      placementType:
        definition.placementType ?? curated.placementType
    };
  });

  BLOCK_DEFINITIONS.splice(0, BLOCK_DEFINITIONS.length, ...merged);
  BLOCK_MAP.clear();
  BLOCK_DEFINITIONS.forEach((definition) => {
    BLOCK_MAP.set(definition.id, definition);
  });
}

export function resetBlockCatalog(): void {
  BLOCK_DEFINITIONS.splice(
    0,
    BLOCK_DEFINITIONS.length,
    ...CURATED_BLOCK_DEFINITIONS.map((definition) => ({ ...definition }))
  );
  BLOCK_MAP.clear();
  BLOCK_DEFINITIONS.forEach((definition) => {
    BLOCK_MAP.set(definition.id, definition);
  });
}

export function isBlockAvailable(blockId: string): boolean {
  return BLOCK_MAP.has(resolveBlockId(blockId));
}

export function getBlockDefinition(blockId: string): BlockDefinition {
  return (
    BLOCK_MAP.get(resolveBlockId(blockId)) ??
    BLOCK_MAP.get("stone_bricks")!
  );
}

export function getMinecraftBlockName(blockId: string): string {
  return getBlockDefinition(blockId).minecraftName;
}

export function getTexturePathForFace(
  definition: BlockDefinition,
  faceName: string
): string | null {
  const textures = definition.textures ?? {};
  return textures[faceName] ?? textures.side ?? textures.all ?? null;
}

export function getTextureTintForFace(
  definition: BlockDefinition,
  faceName: string
): string {
  if (definition.faceTints?.[faceName]) {
    return definition.faceTints[faceName];
  }
  return definition.tint ?? "#ffffff";
}

export function getDefaultBlockState(
  blockId: string
): Record<string, string> {
  return { ...(getBlockDefinition(blockId).defaultState ?? {}) };
}

export function getBlockStateOptions(
  blockId: string
): Record<string, string[]> {
  return getBlockDefinition(blockId).stateOptions ?? {};
}

export function getConnectionType(
  blockId: string
): BlockDefinition["connectionType"] | null {
  return getBlockDefinition(blockId).connectionType ?? null;
}

export function getPlacementType(
  blockId: string
): BlockDefinition["placementType"] | null {
  return getBlockDefinition(blockId).placementType ?? null;
}
